<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { CircleCheck, CirclePlus, Eye, List, RotateCcw } from 'lucide-vue-next'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate, formatDisplayDateTime } from '@yuksalish/ui'
import { formatLotColorSummary } from '../types/lotColor'
import { useShopfloorApi } from '../composables/useShopfloorApi'
import type {
  EmployeeOption,
  LotColorRow,
  LotDetail,
  ReopenReasonCode,
  ShopfloorLot,
  ShopfloorOrder,
  ShopfloorStage,
  ShopfloorStageEntry,
} from '../types/shopfloor'

const route = useRoute()
const { t } = useI18n()
const auth = useAuthStore()
const {
  getOrder,
  listStages,
  listLots,
  createLot,
  recordLotProgress,
  closeLotStage,
  closeStage,
  reopenStage,
  getStageEntries,
  getLotDetail,
  listEmployees,
} = useShopfloorApi()

const orderId = computed(() => Number(route.params.id))
const order = ref<ShopfloorOrder | null>(null)
const stages = ref<ShopfloorStage[]>([])
const lots = ref<ShopfloorLot[]>([])
const employees = ref<EmployeeOption[]>([])
const loading = ref(true)
const error = ref('')

// Supervisor capability (backend requires positions.manage to reopen closed stages).
const canReopen = computed(() => (auth.user?.permissions ?? []).includes('positions.manage'))

const search = ref('')
const statusFilter = ref('')
const stageFilter = ref('')
const page = ref(1)
const perPage = 10

const showCreate = ref(false)
const createForm = reactive({ stage_code: '', quantity: 0, notes: '' })
const creating = ref(false)
const createError = ref('')
const createColors = ref<LotColorRow[]>([])
const createColorsSum = computed(() =>
  createColors.value.reduce((acc, row) => acc + (Number(row.quantity) || 0), 0),
)

function toggleCreateForm() {
  showCreate.value = !showCreate.value
  if (showCreate.value) {
    createForm.stage_code = createForm.stage_code || stages.value[0]?.stage_code || ''
    createForm.quantity = 0
    createForm.notes = ''
    createColors.value = []
    createError.value = ''
  }
}

function addColorRow() {
  createColors.value.push({ color: '', quantity: 0 })
}

function removeColorRow(index: number) {
  createColors.value.splice(index, 1)
}

/**
 * Renders a lot's `color_summary` regardless of the stored shape (the manual
 * form and the kiosk/completion flow write different keys).
 */
function formatColorSummary(summary: ShopfloorLot['color_summary']): string {
  return formatLotColorSummary(summary)
}

const showProgress = ref(false)
const progressForm = reactive({
  lot_number: '',
  stage_code: '',
  completed_delta: 0,
  rejected_delta: 0,
  rework_delta: 0,
  employee_id: null as number | null,
  notes: '',
})
const progressError = ref('')
const submitting = ref(false)
const actionMessage = ref('')

// Lot-level stage close modal
const showLotClose = ref(false)
const lotCloseForm = reactive({
  lot_number: '',
  stage_code: '',
  assigned_employee_name: '',
  employee_id: null as number | null,
  notes: '',
})
const lotCloseError = ref('')
const lotCloseSubmitting = ref(false)

// Order-level stage close modal
const showStageClose = ref(false)
const stageCloseForm = reactive({ stage_code: '', employee_id: null as number | null, notes: '' })
const stageCloseError = ref('')
const stageCloseSubmitting = ref(false)

// Controlled stage reopen modal (supervisor-gated)
const showReopen = ref(false)
const reopenForm = reactive({
  stage_code: '',
  reason_code: '' as ReopenReasonCode | '',
  reason_text: '',
  employee_id: null as number | null,
  notes: '',
})
const reopenError = ref('')
const reopenSubmitting = ref(false)

const reopenReasons: Array<{ code: ReopenReasonCode; label: string }> = [
  { code: 'qc_issue', label: t('shopfloor.orderDetail.reopenReasons.qc_issue') },
  { code: 'operator_error', label: t('shopfloor.orderDetail.reopenReasons.operator_error') },
  { code: 'material_issue', label: t('shopfloor.orderDetail.reopenReasons.material_issue') },
  { code: 'machine_issue', label: t('shopfloor.orderDetail.reopenReasons.machine_issue') },
  { code: 'supervisor_decision', label: t('shopfloor.orderDetail.reopenReasons.supervisor_decision') },
  { code: 'other', label: t('shopfloor.orderDetail.reopenReasons.other') },
]

// Stage entries (audit trail) modal
const showEntries = ref(false)
const entriesStage = ref<ShopfloorStage | null>(null)
const entries = ref<ShopfloorStageEntry[]>([])
const entriesLoading = ref(false)
const entriesError = ref('')

// Lot detail (tracking depth) modal
const showLotDetail = ref(false)
const lotDetail = ref<LotDetail | null>(null)
const lotDetailLoading = ref(false)
const lotDetailError = ref('')

function employeeName(id: number | null): string {
  if (!id) return t('shopfloor.orderDetail.unassigned')
  const found = employees.value.find((e) => e.id === id)
  return found ? `${found.first_name} ${found.last_name}`.trim() : String(id)
}

function stageName(code: string): string {
  return stages.value.find((s) => s.stage_code === code)?.stage_name ?? code
}

function stagePercent(stage: ShopfloorStage): number {
  if (!Number(stage.planned_quantity)) return 0
  return Math.round((Number(stage.completed_quantity || 0) / Number(stage.planned_quantity)) * 100)
}

function actionTypeLabel(type: string): string {
  const key = `shopfloor.orderDetail.entryTypes.${type}`
  const value = t(key)
  return value === key ? type : value
}

function entryDeltas(entry: ShopfloorStageEntry): string {
  const parts: string[] = []
  if (Number(entry.completed_delta) > 0) parts.push(`+${entry.completed_delta}`)
  if (Number(entry.rejected_delta) > 0) parts.push(`✕${entry.rejected_delta}`)
  if (Number(entry.rework_delta) > 0) parts.push(`↻${entry.rework_delta}`)
  return parts.join(' ') || '—'
}

// Stage to highlight (from dashboard bottleneck drill-down, ?stage=code).
const highlightStage = computed(() => String(route.query.stage ?? ''))

const bottleneckStage = computed<ShopfloorStage | null>(() =>
  stages.value
    .filter((stage) => Number(stage.planned_quantity) > 0 && stage.status !== 'completed')
    .reduce<ShopfloorStage | null>((lowest, stage) => {
      if (!lowest) return stage
      const difference = stagePercent(stage) - stagePercent(lowest)
      if (difference < 0) return stage
      if (difference === 0 && stage.operation_sequence < lowest.operation_sequence) return stage
      return lowest
    }, null),
)

const activeLots = computed(() => lots.value.filter((lot) => lot.status === 'active').length)
const completedLots = computed(() => lots.value.filter((lot) => lot.status === 'completed').length)

const filteredLots = computed(() => {
  const term = search.value.trim().toLowerCase()
  return lots.value.filter(
    (lot) =>
      (!term || lot.lot_number.toLowerCase().includes(term)) &&
      (!statusFilter.value || lot.status === statusFilter.value) &&
      (!stageFilter.value || lot.current_stage_code === stageFilter.value),
  )
})
const totalPages = computed(() => Math.max(1, Math.ceil(filteredLots.value.length / perPage)))
const pagedLots = computed(() => {
  const safePage = Math.max(1, Math.floor(page.value))
  return filteredLots.value.slice((safePage - 1) * perPage, safePage * perPage)
})
watch([search, statusFilter, stageFilter], () => { page.value = 1 })
watch(totalPages, (value) => { if (page.value > value) page.value = value })

async function refreshOrderData() {
  ;[order.value, stages.value, lots.value] = await Promise.all([
    getOrder(orderId.value),
    listStages(orderId.value),
    listLots(orderId.value),
  ])
  if (!createForm.stage_code) createForm.stage_code = stages.value[0]?.stage_code || ''
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    await refreshOrderData()
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadEmployees() {
  try {
    employees.value = await listEmployees()
  } catch {
    employees.value = []
  }
}

onMounted(() => {
  void load()
  void loadEmployees()
  window.setTimeout(() => {
    if (highlightStage.value) {
      const el = document.querySelector(`[data-stage="${CSS.escape(highlightStage.value)}"]`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, 400)
})

async function createLotSubmit() {
  createError.value = ''
  if (!createForm.stage_code || createForm.quantity <= 0) {
    createError.value = t('shopfloor.orderDetail.enterStageQuantity')
    return
  }
  const rows = createColors.value.filter((row) => row.color.trim() !== '' || Number(row.quantity) > 0)
  if (rows.length) {
    for (const row of rows) {
      if (!row.color.trim() || Number(row.quantity) <= 0) {
        createError.value = t('shopfloor.orderDetail.colorRequired')
        return
      }
    }
    const sum = rows.reduce((acc, row) => acc + (Number(row.quantity) || 0), 0)
    if (Math.abs(sum - Number(createForm.quantity)) > 0.0001) {
      createError.value = t('shopfloor.orderDetail.colorSumError')
      return
    }
  }
  creating.value = true
  try {
    const lot = await createLot(orderId.value, {
      stage_code: createForm.stage_code,
      quantity: Number(createForm.quantity),
      color_summary: rows.length
        ? rows.map((row) => ({ color: row.color.trim(), quantity: Number(row.quantity) }))
        : undefined,
      notes: createForm.notes.trim() || undefined,
    })
    lots.value = [lot, ...lots.value]
    showCreate.value = false
    actionMessage.value = t('shopfloor.orderDetail.lotCreated', { lot: lot.lot_number })
    createForm.quantity = 0
    createForm.notes = ''
    createColors.value = []
  } catch (cause) {
    createError.value = formatApiError(cause)
  } finally {
    creating.value = false
  }
}

function openProgress(lot: ShopfloorLot) {
  progressForm.lot_number = lot.lot_number
  progressForm.stage_code = lot.current_stage_code
  progressForm.completed_delta = 0
  progressForm.rejected_delta = 0
  progressForm.rework_delta = 0
  progressForm.employee_id = null
  progressForm.notes = ''
  progressError.value = ''
  actionMessage.value = ''
  showProgress.value = true
}

async function submitProgress() {
  progressError.value = ''
  if (!progressForm.employee_id) {
    progressError.value = t('shopfloor.orderDetail.selectEmployee')
    return
  }
  submitting.value = true
  try {
    await recordLotProgress(orderId.value, progressForm.lot_number, progressForm.stage_code, {
      completed_delta: Number(progressForm.completed_delta) || 0,
      rejected_delta: Number(progressForm.rejected_delta) || 0,
      rework_delta: Number(progressForm.rework_delta) || 0,
      employee_id: progressForm.employee_id,
      notes: progressForm.notes.trim() || undefined,
    })
    await refreshOrderData()
    actionMessage.value = t('shopfloor.orderDetail.progressRecorded', { lot: progressForm.lot_number })
    showProgress.value = false
  } catch (cause) {
    progressError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function openLotClose(lot: ShopfloorLot) {
  lotCloseForm.lot_number = lot.lot_number
  lotCloseForm.stage_code = lot.current_stage_code
  lotCloseForm.assigned_employee_name = lot.assigned_employee_name?.trim() ?? ''
  lotCloseForm.employee_id = lot.assigned_employee_id ?? null
  lotCloseForm.notes = ''
  lotCloseError.value = ''
  showLotClose.value = true
}

async function submitLotClose() {
  lotCloseError.value = ''
  if (!lotCloseForm.assigned_employee_name && !lotCloseForm.employee_id) {
    lotCloseError.value = t('shopfloor.orderDetail.selectClosingEmployee')
    return
  }
  lotCloseSubmitting.value = true
  try {
    await closeLotStage(orderId.value, lotCloseForm.lot_number, lotCloseForm.stage_code, {
      employee_id: lotCloseForm.employee_id || undefined,
      notes: lotCloseForm.notes.trim() || undefined,
    })
    await refreshOrderData()
    actionMessage.value = t('shopfloor.orderDetail.stageClosed', { lot: lotCloseForm.lot_number })
    showLotClose.value = false
  } catch (cause) {
    lotCloseError.value = formatApiError(cause)
  } finally {
    lotCloseSubmitting.value = false
  }
}

function openStageClose(stage: ShopfloorStage) {
  stageCloseForm.stage_code = stage.stage_code
  stageCloseForm.employee_id = null
  stageCloseForm.notes = ''
  stageCloseError.value = ''
  showStageClose.value = true
}

async function submitStageClose() {
  stageCloseError.value = ''
  if (!stageCloseForm.employee_id) {
    stageCloseError.value = t('shopfloor.orderDetail.selectClosingEmployee')
    return
  }
  stageCloseSubmitting.value = true
  try {
    stages.value = await closeStage(orderId.value, stageCloseForm.stage_code, {
      employee_id: stageCloseForm.employee_id,
      notes: stageCloseForm.notes.trim() || undefined,
    })
    await refreshOrderData()
    actionMessage.value = t('shopfloor.orderDetail.stageClosedAll', { stage: stageName(stageCloseForm.stage_code) })
    showStageClose.value = false
  } catch (cause) {
    stageCloseError.value = formatApiError(cause)
  } finally {
    stageCloseSubmitting.value = false
  }
}

function openReopen(stage: ShopfloorStage) {
  reopenForm.stage_code = stage.stage_code
  reopenForm.reason_code = ''
  reopenForm.reason_text = ''
  reopenForm.employee_id = null
  reopenForm.notes = ''
  reopenError.value = ''
  showReopen.value = true
}

async function submitReopen() {
  reopenError.value = ''
  if (!reopenForm.reason_code || !reopenForm.reason_text.trim()) {
    reopenError.value = t('shopfloor.orderDetail.reopenRequiresReason')
    return
  }
  reopenSubmitting.value = true
  try {
    stages.value = await reopenStage(orderId.value, reopenForm.stage_code, {
      reason_code: reopenForm.reason_code,
      reason_text: reopenForm.reason_text.trim(),
      employee_id: reopenForm.employee_id,
      notes: reopenForm.notes.trim() || undefined,
    })
    await refreshOrderData()
    actionMessage.value = t('shopfloor.orderDetail.stageReopened', { stage: stageName(reopenForm.stage_code) })
    showReopen.value = false
  } catch (cause) {
    reopenError.value = formatApiError(cause)
  } finally {
    reopenSubmitting.value = false
  }
}

async function openEntries(stage: ShopfloorStage) {
  entriesStage.value = stage
  entries.value = []
  entriesError.value = ''
  showEntries.value = true
  entriesLoading.value = true
  try {
    entries.value = await getStageEntries(orderId.value, stage.stage_code)
  } catch (cause) {
    entriesError.value = formatApiError(cause)
  } finally {
    entriesLoading.value = false
  }
}

async function openLotDetail(lot: ShopfloorLot) {
  lotDetail.value = null
  lotDetailError.value = ''
  showLotDetail.value = true
  lotDetailLoading.value = true
  try {
    lotDetail.value = await getLotDetail(orderId.value, lot.lot_number)
  } catch (cause) {
    lotDetailError.value = formatApiError(cause)
  } finally {
    lotDetailLoading.value = false
  }
}

function lotStageStatusClass(status: string): string {
  if (status === 'closed') return 'status-closed'
  if (status === 'in_progress') return 'status-active'
  return ''
}

function lotStatusLabel(status: string): string {
  const key = `shopfloor.orderDetail.lotStatuses.${status}`
  const value = t(key)
  return value === key ? status : value
}

function basketProgressClass(status: string | null | undefined): string {
  if (status === 'completed') return 'basket-badge--completed'
  if (status === 'advanced') return 'basket-badge--advanced'
  if (status === 'cancelled') return 'basket-badge--cancelled'
  return 'basket-badge--open'
}

function stageStatusLabel(status: string): string {
  const key = `shopfloor.orderDetail.stageStatuses.${status}`
  const value = t(key)
  return value === key ? status : value
}

function entryActionClass(action: string): string {
  if (action === 'close') return 'status-closed'
  if (action === 'reopen') return 'status-reopen'
  if (action === 'create') return 'status-create'
  return 'status-active'
}
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/orders">← {{ t('shopfloor.orderDetail.backToOrders') }}</RouterLink>
    <div>
      <p class="eyebrow">{{ order?.mo_code }}</p>
      <h2>{{ t('shopfloor.orderDetail.title') }}</h2>
      <p>{{ order?.product_name }} · {{ order?.department_name }}</p>
    </div>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('shopfloor.orderDetail.loadingOrder') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('shopfloor.state.retry') }}</button>
  </div>

  <template v-else-if="order">
    <div class="order-actions">
      <RouterLink class="btn-primary" :to="`/knitting-output?order=${order.id}`">{{ t('shopfloor.orderDetail.recordOutput') }}</RouterLink>
    </div>
    <!-- Order summary -->
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-value">{{ order.planned_quantity.toLocaleString() }}</span>
        <span class="summary-label">{{ t('shopfloor.orderDetail.planned') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ Number(order.produced_quantity || 0).toLocaleString() }}</span>
        <span class="summary-label">{{ t('shopfloor.orderDetail.produced') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ lots.length }}</span>
        <span class="summary-label">{{ t('shopfloor.orderDetail.totalLots') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ activeLots }}</span>
        <span class="summary-label">{{ t('shopfloor.orderDetail.activeLots') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ completedLots }}</span>
        <span class="summary-label">{{ t('shopfloor.orderDetail.completedLots') }}</span>
      </div>
    </div>

    <section v-if="order.variants?.length" class="panel variant-plan">
      <div class="panel-header">
        <div><p class="eyebrow">{{ t('shopfloor.orderDetail.simplifiedPlan') }}</p><h3>{{ t('shopfloor.orderDetail.variantPlan') }}</h3></div>
        <strong>{{ t('shopfloor.orderDetail.variantPlanTotal', { count: order.planned_quantity }) }}</strong>
      </div>
      <div class="variant-grid">
        <article v-for="variant in order.variants" :key="variant.id ?? `${variant.color_name}-${variant.size_label}`">
          <strong>{{ variant.color_name }}</strong>
          <span>{{ t('shopfloor.orderDetail.size') }}: {{ variant.size_label }}</span>
          <b>{{ variant.planned_quantity.toLocaleString() }}</b>
        </article>
      </div>
    </section>

    <!-- Stage board -->
    <section class="panel">
      <div class="panel-header">
        <h3>{{ t('shopfloor.orderDetail.stageProgress') }}</h3>
        <button v-if="!loading && !error" class="btn-secondary" @click="toggleCreateForm">
          {{ showCreate ? t('shopfloor.orderDetail.cancel') : t('shopfloor.orderDetail.createLot') }}
        </button>
      </div>

      <!-- Create lot form -->
      <form v-if="showCreate" class="create-form" @submit.prevent="createLotSubmit">
        <label>{{ t('shopfloor.orderDetail.startingStage') }}
          <select v-model="createForm.stage_code" required>
            <option v-for="stage in stages" :key="stage.stage_code" :value="stage.stage_code">
              {{ stage.stage_name }}
            </option>
          </select>
        </label>
        <label>{{ t('shopfloor.orderDetail.quantity') }}
          <input v-model.number="createForm.quantity" type="number" min="1" required />
        </label>
        <label>{{ t('shopfloor.orderDetail.notes') }}
          <input v-model="createForm.notes" type="text" />
        </label>
        <button class="btn-primary" type="submit" :disabled="creating">
          {{ creating ? t('shopfloor.orderDetail.creating') : t('shopfloor.orderDetail.createLot') }}
        </button>
        <small v-if="createError" class="error create-form__error">{{ createError }}</small>

        <!-- Color variant entries (optional) -->
        <div class="color-editor">
          <div class="color-editor__head">
            <strong>{{ t('shopfloor.orderDetail.colorVariants') }}</strong>
            <span class="color-editor__hint">
              {{ t('shopfloor.orderDetail.colorsSumHint', { sum: createColorsSum, quantity: createForm.quantity || 0 }) }}
            </span>
          </div>
          <div v-for="(row, index) in createColors" :key="index" class="color-row">
            <input
              v-model="row.color"
              type="text"
              :placeholder="t('shopfloor.orderDetail.colorName')"
              class="color-row__name"
            />
            <input
              v-model.number="row.quantity"
              type="number"
              min="0"
              :placeholder="t('shopfloor.orderDetail.colorQuantity')"
              class="color-row__qty"
            />
            <button
              type="button"
              class="color-row__remove"
              :aria-label="t('shopfloor.orderDetail.removeColor')"
              :title="t('shopfloor.orderDetail.removeColor')"
              @click="removeColorRow(index)"
            >
              ✕
            </button>
          </div>
          <button type="button" class="btn-secondary" @click="addColorRow">
            + {{ t('shopfloor.orderDetail.addColor') }}
          </button>
        </div>
      </form>

      <div v-if="stages.length === 0" class="empty-inline">{{ t('shopfloor.orderDetail.noStages') }}</div>
      <ol v-else class="stage-list">
        <li
          v-for="stage in stages"
          :key="stage.stage_code"
          :data-stage="stage.stage_code"
          :class="['stage-item', {
            bottleneck: bottleneckStage?.stage_code === stage.stage_code,
            highlighted: highlightStage === stage.stage_code,
            closed: stage.status === 'completed',
          }]"
        >
          <div class="stage-main">
            <span class="stage-sequence">{{ stage.operation_sequence }}</span>
            <div>
              <strong>{{ stage.stage_name }}</strong>
              <small v-if="stage.assigned_employee_name"> · {{ stage.assigned_employee_name }}</small>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: stagePercent(stage) + '%' }" />
              </div>
            </div>
          </div>
          <div class="stage-meta">
            <span>{{ stage.completed_quantity.toLocaleString() }} / {{ stage.planned_quantity.toLocaleString() }}</span>
            <span class="stage-percent">{{ stagePercent(stage) }}%</span>
            <div class="row-actions">
              <button class="link-action icon-action" :title="t('shopfloor.orderDetail.viewEntries')" :aria-label="t('shopfloor.orderDetail.viewEntries')" @click="openEntries(stage)">
                <List :size="18" aria-hidden="true" />
              </button>
              <button v-if="stage.status !== 'completed'" class="link-action icon-action" :title="t('shopfloor.orderDetail.closeStage')" :aria-label="t('shopfloor.orderDetail.closeStage')" @click="openStageClose(stage)">
                <CircleCheck :size="18" aria-hidden="true" />
              </button>
              <button v-if="stage.status === 'completed' && canReopen" class="link-action icon-action danger" :title="t('shopfloor.orderDetail.reopenStage')" :aria-label="t('shopfloor.orderDetail.reopenStage')" @click="openReopen(stage)">
                <RotateCcw :size="18" aria-hidden="true" />
              </button>
            </div>
          </div>
        </li>
      </ol>
    </section>

    <!-- Lot queue -->
    <section class="panel">
      <h3>{{ t('shopfloor.orderDetail.lotQueue') }}</h3>
      <div class="filters">
        <input v-model="search" type="search" :placeholder="t('shopfloor.orderDetail.lotNumber')" class="search-input" />
        <select v-model="statusFilter" class="filter-select">
          <option value="">{{ t('shopfloor.orderDetail.allStatuses') }}</option>
          <option value="active">{{ t('shopfloor.orderDetail.active') }}</option>
          <option value="completed">{{ t('shopfloor.orderDetail.completed') }}</option>
          <option value="cancelled">{{ t('shopfloor.orderDetail.cancelled') }}</option>
        </select>
        <select v-model="stageFilter" class="filter-select">
          <option value="">{{ t('shopfloor.orderDetail.allStages') }}</option>
          <option v-for="stage in stages" :key="stage.stage_code" :value="stage.stage_code">
            {{ stage.stage_name }}
          </option>
        </select>
      </div>

      <div v-if="filteredLots.length === 0" class="empty-inline">{{ t('shopfloor.orderDetail.noLotsMatch') }}</div>
      <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('shopfloor.orderDetail.lot') }}</th>
            <th>{{ t('shopfloor.knittingOutput.basket') }}</th>
            <th>{{ t('shopfloor.orderDetail.status') }}</th>
            <th>{{ t('shopfloor.orderDetail.currentStage') }}</th>
            <th>{{ t('shopfloor.orderDetail.initial') }}</th>
            <th>{{ t('shopfloor.orderDetail.current') }}</th>
            <th>{{ t('shopfloor.orderDetail.colorSummary') }}</th>
            <th>{{ t('shopfloor.orderDetail.created') }}</th>
            <th>{{ t('shopfloor.orderDetail.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="lot in pagedLots" :key="lot.id">
            <td>
              <div class="lot-cell">
                <button
                  type="button"
                  class="queue-text-action cell-code"
                  :title="t('shopfloor.orderDetail.details')"
                  :aria-label="`${t('shopfloor.orderDetail.details')}: ${lot.lot_number}`"
                  @click="openLotDetail(lot)"
                >{{ lot.lot_number }}</button>
                <small v-if="lot.status === 'active' && lot.assigned_employee_name" class="lot-worker">
                  {{ lot.assigned_employee_name }}
                </small>
              </div>
            </td>
            <td>
              <span
                v-if="lot.basket_number || lot.basket_code"
                :class="['basket-badge', basketProgressClass(lot.basket_status)]"
              >{{ lot.basket_number || lot.basket_code }}</span>
              <span v-else class="cell-muted">вЂ”</span>
            </td>
            <td>
              <span :class="['status-badge', lot.status === 'active' ? 'status-active' : '']">
                {{ lotStatusLabel(lot.status) }}
              </span>
            </td>
            <td>
              <button
                v-if="lot.status === 'active'"
                type="button"
                class="queue-text-action stage-text-action"
                :title="t('shopfloor.orderDetail.closeStage')"
                :aria-label="`${t('shopfloor.orderDetail.closeStage')}: ${stageName(lot.current_stage_code)}`"
                @click="openLotClose(lot)"
              >{{ stageName(lot.current_stage_code) }}</button>
              <span v-else>{{ stageName(lot.current_stage_code) }}</span>
            </td>
            <td>{{ lot.initial_quantity.toLocaleString() }}</td>
            <td>{{ lot.current_quantity.toLocaleString() }}</td>
            <td class="cell-muted">{{ formatColorSummary(lot.color_summary) || '—' }}</td>
            <td>{{ formatDisplayDate(lot.created_at) || '—' }}</td>
            <td>
              <div class="row-actions">
                <button class="link-action icon-action" :title="t('shopfloor.orderDetail.details')" :aria-label="t('shopfloor.orderDetail.details')" @click="openLotDetail(lot)">
                  <Eye :size="18" aria-hidden="true" />
                </button>
                <button v-if="lot.status === 'active'" class="link-action icon-action" :title="t('shopfloor.orderDetail.progress')" :aria-label="t('shopfloor.orderDetail.progress')" @click="openProgress(lot)">
                  <CirclePlus :size="18" aria-hidden="true" />
                </button>
                <button v-if="lot.status === 'active'" class="link-action icon-action danger" :title="t('shopfloor.orderDetail.closeStage')" :aria-label="t('shopfloor.orderDetail.closeStage')" @click="openLotClose(lot)">
                  <CircleCheck :size="18" aria-hidden="true" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <div v-if="totalPages > 1" class="pagination">
        <span class="page-info">{{ t('shopfloor.orderDetail.pageOf', { page, total: totalPages }) }}</span>
        <div class="page-buttons">
          <button :disabled="page <= 1" @click="page--">{{ t('shopfloor.orderDetail.previous') }}</button>
          <button :disabled="page >= totalPages" @click="page++">{{ t('shopfloor.orderDetail.next') }}</button>
        </div>
      </div>
    </section>

    <!-- Progress dialog -->
    <div v-if="showProgress" class="modal-overlay" @click.self="showProgress = false">
      <form class="modal" @submit.prevent="submitProgress">
        <h3>{{ t('shopfloor.orderDetail.recordProgress') }} — {{ progressForm.lot_number }}</h3>
        <p class="modal-sub">{{ t('shopfloor.orderDetail.stageLabel') }} {{ stageName(progressForm.stage_code) }}</p>
        <label>{{ t('shopfloor.orderDetail.completedQty') }}
          <input v-model.number="progressForm.completed_delta" type="number" min="0" />
        </label>
        <label>{{ t('shopfloor.orderDetail.rejected') }}
          <input v-model.number="progressForm.rejected_delta" type="number" min="0" />
        </label>
        <label>{{ t('shopfloor.orderDetail.rework') }}
          <input v-model.number="progressForm.rework_delta" type="number" min="0" />
        </label>
        <label>{{ t('shopfloor.orderDetail.employee') }}
          <select v-model.number="progressForm.employee_id" required>
            <option :value="null" disabled>{{ t('shopfloor.orderDetail.selectEmployeePlaceholder') }}</option>
            <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.first_name }} {{ e.last_name }}</option>
          </select>
        </label>
        <label>{{ t('shopfloor.orderDetail.notes') }}
          <input v-model="progressForm.notes" type="text" />
        </label>
        <small v-if="progressError" class="error">{{ progressError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showProgress = false">{{ t('shopfloor.orderDetail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">{{ t('shopfloor.orderDetail.save') }}</button>
        </div>
      </form>
    </div>

    <!-- Lot stage close dialog -->
    <div v-if="showLotClose" class="modal-overlay" @click.self="showLotClose = false">
      <form class="modal" @submit.prevent="submitLotClose">
        <h3>{{ t('shopfloor.orderDetail.closeStageTitle') }} — {{ lotCloseForm.lot_number }}</h3>
        <p class="modal-sub close-stage-name">
          {{ t('shopfloor.orderDetail.stageLabel') }}
          <strong>{{ stageName(lotCloseForm.stage_code) }}</strong>
        </p>
        <p v-if="lotCloseForm.assigned_employee_name" class="modal-sub">
          {{ t('shopfloor.orderDetail.employee') }}: {{ lotCloseForm.assigned_employee_name }}
        </p>
        <label v-else>{{ t('shopfloor.orderDetail.employee') }}
          <select v-model.number="lotCloseForm.employee_id" required>
            <option :value="null" disabled>{{ t('shopfloor.orderDetail.selectEmployeePlaceholder') }}</option>
            <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.first_name }} {{ e.last_name }}</option>
          </select>
        </label>
        <label>{{ t('shopfloor.orderDetail.notes') }}
          <input v-model="lotCloseForm.notes" type="text" />
        </label>
        <small v-if="lotCloseError" class="error">{{ lotCloseError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showLotClose = false">{{ t('shopfloor.orderDetail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="lotCloseSubmitting">{{ t('shopfloor.orderDetail.confirm') }}</button>
        </div>
      </form>
    </div>

    <!-- Order-level stage close dialog -->
    <div v-if="showStageClose" class="modal-overlay" @click.self="showStageClose = false">
      <form class="modal" @submit.prevent="submitStageClose">
        <h3>{{ t('shopfloor.orderDetail.closeStageTitle') }} — {{ stageName(stageCloseForm.stage_code) }}</h3>
        <p class="modal-sub">{{ t('shopfloor.orderDetail.closeStageHint') }}</p>
        <label>{{ t('shopfloor.orderDetail.employee') }}
          <select v-model.number="stageCloseForm.employee_id" required>
            <option :value="null" disabled>{{ t('shopfloor.orderDetail.selectEmployeePlaceholder') }}</option>
            <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.first_name }} {{ e.last_name }}</option>
          </select>
        </label>
        <label>{{ t('shopfloor.orderDetail.notes') }}
          <input v-model="stageCloseForm.notes" type="text" />
        </label>
        <small v-if="stageCloseError" class="error">{{ stageCloseError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showStageClose = false">{{ t('shopfloor.orderDetail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="stageCloseSubmitting">{{ t('shopfloor.orderDetail.confirm') }}</button>
        </div>
      </form>
    </div>

    <!-- Controlled stage reopen dialog -->
    <div v-if="showReopen" class="modal-overlay" @click.self="showReopen = false">
      <form class="modal modal-wide" @submit.prevent="submitReopen">
        <h3>{{ t('shopfloor.orderDetail.reopenStageTitle') }} — {{ stageName(reopenForm.stage_code) }}</h3>
        <p class="modal-sub">{{ t('shopfloor.orderDetail.reopenHint') }}</p>
        <label>{{ t('shopfloor.orderDetail.reopenReason') }}
          <select v-model="reopenForm.reason_code" required>
            <option value="" disabled>{{ t('shopfloor.orderDetail.selectReason') }}</option>
            <option v-for="r in reopenReasons" :key="r.code" :value="r.code">{{ r.label }}</option>
          </select>
        </label>
        <label>{{ t('shopfloor.orderDetail.reopenReasonText') }}
          <textarea v-model="reopenForm.reason_text" rows="3" required />
        </label>
        <label>{{ t('shopfloor.orderDetail.employee') }}
          <select v-model.number="reopenForm.employee_id">
            <option :value="null">{{ t('shopfloor.orderDetail.unassigned') }}</option>
            <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.first_name }} {{ e.last_name }}</option>
          </select>
        </label>
        <small v-if="reopenError" class="error">{{ reopenError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showReopen = false">{{ t('shopfloor.orderDetail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="reopenSubmitting">{{ t('shopfloor.orderDetail.confirm') }}</button>
        </div>
      </form>
    </div>

    <!-- Stage entries (audit trail) dialog -->
    <div v-if="showEntries" class="modal-overlay" @click.self="showEntries = false">
      <div class="modal modal-wide modal-audit">
        <h3>{{ t('shopfloor.orderDetail.entriesTitle') }} — {{ entriesStage?.stage_name }}</h3>
        <div v-if="entriesLoading" class="state-message"><p>{{ t('shopfloor.state.loading') }}</p></div>
        <div v-else-if="entriesError" class="state-message error"><p>{{ entriesError }}</p></div>
        <div v-else-if="entries.length === 0" class="empty-inline">{{ t('shopfloor.orderDetail.noEntries') }}</div>
        <div v-else class="table-scroll">
        <table class="data-table entry-table">
          <thead>
            <tr>
              <th>{{ t('shopfloor.orderDetail.entryAction') }}</th>
              <th>{{ t('shopfloor.orderDetail.entryDeltas') }}</th>
              <th>{{ t('shopfloor.orderDetail.entryEmployee') }}</th>
              <th>{{ t('shopfloor.orderDetail.entryLot') }}</th>
              <th>{{ t('shopfloor.orderDetail.entryTime') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in entries" :key="entry.id">
              <td>
                <span :class="['status-badge', entryActionClass(entry.action_type)]">{{ actionTypeLabel(entry.action_type) }}</span>
              </td>
              <td class="cell-code">{{ entryDeltas(entry) }}</td>
              <td>{{ entry.employee_name ?? '—' }}</td>
              <td>{{ entry.lot_number ?? '—' }}</td>
              <td>{{ formatDisplayDateTime(entry.created_at) || '—' }}</td>
            </tr>
          </tbody>
        </table>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showEntries = false">{{ t('shopfloor.orderDetail.close') }}</button>
        </div>
      </div>
    </div>

    <!-- Lot detail (tracking depth) dialog -->
    <div v-if="showLotDetail" class="modal-overlay" @click.self="showLotDetail = false">
      <div class="modal modal-wide modal-audit">
        <div v-if="lotDetailLoading" class="state-message"><p>{{ t('shopfloor.state.loading') }}</p></div>
        <div v-else-if="lotDetailError" class="state-message error"><p>{{ lotDetailError }}</p></div>
        <template v-else-if="lotDetail">
          <h3>{{ t('shopfloor.orderDetail.lotDetailsTitle') }} — {{ lotDetail.lot.lot_number }}</h3>
          <p class="modal-sub">
            {{ lotDetail.lot.initial_quantity.toLocaleString() }} → {{ lotDetail.lot.current_quantity.toLocaleString() }} ·
            {{ t('shopfloor.orderDetail.currentStage') }}: {{ stageName(lotDetail.lot.current_stage_code) }}
          </p>
          <p v-if="formatColorSummary(lotDetail.lot.color_summary)" class="modal-sub">
            {{ t('shopfloor.orderDetail.colorSummary') }}: {{ formatColorSummary(lotDetail.lot.color_summary) }}
          </p>
          <h4 class="section-title">{{ t('shopfloor.orderDetail.lotStages') }}</h4>
          <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ t('shopfloor.orderDetail.stage') }}</th>
                <th>{{ t('shopfloor.orderDetail.status') }}</th>
                <th>{{ t('shopfloor.orderDetail.planned') }}</th>
                <th>{{ t('shopfloor.orderDetail.completedQty') }}</th>
                <th>{{ t('shopfloor.orderDetail.rejected') }}</th>
                <th>{{ t('shopfloor.orderDetail.rework') }}</th>
                <th>{{ t('shopfloor.orderDetail.entryEmployee') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in lotDetail.stages" :key="row.id">
                <td class="cell-code">{{ row.operation_sequence }}</td>
                <td>{{ row.stage_name }}</td>
                <td>
                  <span :class="['status-badge', lotStageStatusClass(row.status)]">{{ stageStatusLabel(row.status) }}</span>
                </td>
                <td>{{ row.planned_quantity.toLocaleString() }}</td>
                <td>{{ row.completed_quantity.toLocaleString() }}</td>
                <td>{{ row.rejected_quantity.toLocaleString() }}</td>
                <td>{{ row.rework_quantity.toLocaleString() }}</td>
                <td>{{ row.assigned_employee_name ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
          </div>
          <h4 class="section-title">{{ t('shopfloor.orderDetail.lotEntries') }}</h4>
          <div v-if="lotDetail.entries.length === 0" class="empty-inline">{{ t('shopfloor.orderDetail.noEntries') }}</div>
          <div v-else class="table-scroll">
          <table class="data-table entry-table">
            <thead>
              <tr>
                <th>{{ t('shopfloor.orderDetail.entryAction') }}</th>
                <th>{{ t('shopfloor.orderDetail.entryDeltas') }}</th>
                <th>{{ t('shopfloor.orderDetail.entryEmployee') }}</th>
                <th>{{ t('shopfloor.orderDetail.entryStage') }}</th>
                <th>{{ t('shopfloor.orderDetail.entryTime') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in lotDetail.entries" :key="entry.id">
                <td>
                  <span :class="['status-badge', entryActionClass(entry.action_type)]">{{ actionTypeLabel(entry.action_type) }}</span>
                </td>
                <td class="cell-code">{{ entryDeltas(entry) }}</td>
                <td>{{ entry.employee_name ?? '—' }}</td>
                <td>{{ entry.stage_name }}</td>
                <td>{{ formatDisplayDateTime(entry.created_at) || '—' }}</td>
              </tr>
            </tbody>
          </table>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="showLotDetail = false">{{ t('shopfloor.orderDetail.close') }}</button>
          </div>
        </template>
      </div>
    </div>

    <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
  </template>
</template>

<style scoped>
.page-heading {
  margin-bottom: 20px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }
.order-actions { display: flex; justify-content: flex-end; margin: -48px 0 18px; }
.order-actions .btn-primary { text-decoration: none; }
.back {
  display: inline-block;
  margin-bottom: 12px;
  color: #27705d;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}
.back:hover { text-decoration: underline; }

.state-message {
  padding: 40px 0;
  text-align: center;
  color: #5f6b6a;
}
.state-message.error { color: #b33a3a; }

.summary-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}
.summary-card {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  padding: 16px 20px;
  text-align: center;
}
.summary-value { display: block; font-size: 24px; font-weight: 800; color: #1a2b26; }
.summary-label { display: block; font-size: 11px; color: #65736e; text-transform: uppercase; font-weight: 600; margin-top: 2px; }

.panel {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
}
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.panel h3 { margin: 0 0 12px; font-size: 15px; font-weight: 700; color: #1a2b26; }
.panel-header h3 { margin: 0; }
.variant-grid { display: grid; grid-template-columns: repeat(auto-fit,minmax(170px,1fr)); gap: 10px; }
.variant-grid article { display: grid; grid-template-columns: 1fr auto; gap: 5px 12px; padding: 12px; border: 1px solid #e2e6e3; border-radius: 8px; background: #f9fbfa; }
.variant-grid article strong { min-width: 0; overflow-wrap: anywhere; }
.variant-grid article span { grid-column: 1; color: #65736e; font-size: 12px; }
.variant-grid article b { grid-column: 2; grid-row: 1 / 3; align-self: center; font-size: 20px; }

.empty-inline { color: #8b9a95; font-size: 13px; font-style: italic; padding: 8px 0; }

.create-form {
  display: grid;
  grid-template-columns: repeat(3, 1fr) auto;
  gap: 12px;
  align-items: end;
  padding: 16px;
  background: #f6f8f7;
  border-radius: 8px;
  margin-bottom: 16px;
}
.create-form label, .modal label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
  gap: 4px;
}
.create-form input, .create-form select, .modal input {
  padding: 8px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
}
.create-form__error {
  grid-column: 1 / -1;
}
.color-editor {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid #e2e6e3;
  border-radius: 8px;
  background: #fff;
}
.color-editor__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.color-editor__head strong {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: #3d5a4f;
}
.color-editor__hint {
  font-size: 12px;
  color: #65736e;
}
.color-row {
  display: grid;
  grid-template-columns: 1fr 120px auto;
  gap: 8px;
}
.color-row input {
  padding: 8px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
}
.color-row__remove {
  display: inline-grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid #e1b1aa;
  border-radius: 6px;
  color: #b33a3a;
  background: #fff;
  cursor: pointer;
}
.color-editor .btn-secondary {
  align-self: flex-start;
}
.cell-muted {
  color: #65736e;
}
.lot-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}
.lot-worker {
  color: #7a8883;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.25;
}

.stage-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.stage-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f2f1;
}
.stage-item:last-child { border-bottom: none; }
.stage-item.bottleneck { background: #fffdf5; border-left: 3px solid #f0c040; }
.stage-item.highlighted { background: #fdf3e6; border-left: 3px solid #b3472d; }
.stage-item.closed { opacity: 0.6; }
.stage-main { display: flex; align-items: center; gap: 12px; flex: 1; }
.stage-sequence {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #eaf0ed;
  color: #1f5c4b;
  font-size: 12px;
  font-weight: 700;
}
.stage-main strong { font-size: 13px; color: #1a2b26; }
.stage-main small { color: #65736e; font-size: 12px; }
.stage-main .progress-bar { width: 160px; margin-top: 4px; }
.stage-meta { display: flex; align-items: center; gap: 12px; }
.stage-meta span { font-size: 12px; color: #65736e; }
.stage-percent { font-weight: 700; color: #1a2b26; }

.filters {
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
}
.search-input {
  flex: 1;
  padding: 8px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
}
.filter-select {
  padding: 8px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  min-width: 150px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}
.data-table th {
  text-align: left;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #65736e;
  background: #f6f8f7;
  border-bottom: 1px solid #e2e6e3;
}
.data-table td {
  padding: 10px 12px;
  font-size: 13px;
  color: #1a2b26;
  border-bottom: 1px solid #f0f2f1;
}
.data-table tr:last-child td { border-bottom: none; }
.cell-code { font-weight: 700; font-family: monospace; }
.queue-text-action {
  padding: 0;
  border: 0;
  background: transparent;
  color: #1f5c4b;
  cursor: pointer;
  text-align: left;
}
.queue-text-action:hover { text-decoration: underline; }
.queue-text-action:focus-visible {
  border-radius: 3px;
  outline: 2px solid #2c8069;
  outline-offset: 3px;
}
.stage-text-action { font: inherit; font-weight: 600; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: #eaf0ed;
  color: #3d5a4f;
}

.basket-badge {
  display: inline-flex;
  align-items: center;
  min-height: 1.7rem;
  padding: 0.2rem 0.65rem;
  border: 1px solid transparent;
  border-radius: 999px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.78rem;
  font-weight: 800;
  white-space: nowrap;
}

.basket-badge--open { background: #fff4cf; border-color: #f0d36b; color: #76520a; }
.basket-badge--advanced { background: #dbeafe; border-color: #93c5fd; color: #1e4f91; }
.basket-badge--completed { background: #dcefe7; border-color: #86c9b2; color: #1f5c4b; }
.basket-badge--cancelled { background: #f3e3e3; border-color: #e2aaaa; color: #9c3b3b; }
.status-active { background: #dcefe7; color: #1f5c4b; }

.row-actions { display: flex; gap: 8px; }
.link-action {
  color: #27705d;
  font-weight: 600;
  font-size: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.link-action:hover { text-decoration: underline; }
.link-action.danger { color: #b33a3a; }

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 14px;
}
.page-info { font-size: 13px; color: #65736e; }
.page-buttons { display: flex; gap: 8px; }
.page-buttons button {
  padding: 6px 16px;
  background: #fff;
  color: #27705d;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.page-buttons button:disabled { opacity: 0.4; cursor: default; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 20, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal {
  width: 360px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.modal h3 { margin: 0; font-size: 16px; font-weight: 700; }
.modal-sub { margin: 0; font-size: 13px; color: #65736e; }
.close-stage-name {
  display: flex;
  align-items: center;
  gap: 8px;
}
.close-stage-name strong {
  display: inline-flex;
  padding: 5px 10px;
  border: 1px solid #f0a54a;
  border-radius: 6px;
  background: #fff4e5;
  color: #9a4d00;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
}
.modal-wide { width: 640px; max-width: 92vw; }
/* Audit dialogs (lot detail, stage entries): a touch wider, and every cell or
   header stays on ONE line — the `.table-scroll` wrapper pans instead. */
.modal-audit { width: min(940px, 94vw); max-width: 94vw; }
.modal-audit .table-scroll { min-width: 0; }
.modal-audit .data-table th,
.modal-audit .data-table td {
  white-space: nowrap;
}
.modal textarea {
  padding: 8px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
  font-family: inherit;
  resize: vertical;
}
.modal select {
  padding: 8px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
.section-title {
  margin: 18px 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: #1a2b26;
}
.entry-table th, .entry-table td { padding: 8px 10px; }
.status-closed { background: #f3e3e3; color: #9c3b3b; }
.status-reopen { background: #fdf0d7; color: #9a6a12; }
.status-create { background: #e7edf6; color: #3b5f8f; }
.stage-meta .row-actions { gap: 10px; }

.error { color: #b33a3a; font-size: 12px; }
.action-message {
  padding: 12px 16px;
  background: #dcefe7;
  color: #1f5c4b;
  border-radius: 8px;
  font-size: 13px;
}

.btn-primary {
  display: inline-block;
  padding: 8px 20px;
  background: #27705d;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary:disabled { opacity: 0.5; cursor: default; }
.btn-primary:hover:not(:disabled) { background: #1f5c4b; }
.btn-secondary {
  padding: 8px 16px;
  background: #fff;
  color: #27705d;
  border: 1px solid #27705d;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.btn-secondary:hover { background: #f0f5f3; }

.progress-bar {
  display: inline-block;
  width: 100%;
  height: 6px;
  background: #e2e6e3;
  border-radius: 3px;
}
.progress-fill {
  height: 100%;
  background: #27705d;
  border-radius: 3px;
  transition: width 0.3s;
}

@media (max-width: 900px) {
  .summary-row { grid-template-columns: repeat(3, 1fr); }
  .create-form { grid-template-columns: 1fr; }
  .order-actions { justify-content: flex-start; margin: 0 0 18px; }
}
</style>
